import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '../App';
import { 
  Settings as SettingsIcon, Shield, Globe, Key, Save, AlertTriangle, 
  ExternalLink, CheckCircle, XCircle, Loader, Plane, Ship, Satellite,
  Wifi, WifiOff, RefreshCw, Trash2, Eye, EyeOff, Activity, Bell,
  Clock, Zap, AlertCircle, TrendingUp, TrendingDown, X, Heart,
  Rss, Plus, Search, MessageCircle, Send, Twitter, Instagram as InstagramIcon,
  Users, UserPlus, UserMinus, UserCheck, Mail, Building, BarChart3
} from 'lucide-react';

export default function Settings() {
  const [offlineMode, setOfflineMode] = useState(false);
  const [language, setLanguage] = useState('en');
  const [saving, setSaving] = useState(false);
  const [dataSources, setDataSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [healthAlerts, setHealthAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState(null);
  const [activeTab, setActiveTab] = useState('tracking'); // tracking, osint, news, general
  
  // Custom news sources state
  const [customNewsSources, setCustomNewsSources] = useState([]);
  const [newSourceName, setNewSourceName] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [addingSource, setAddingSource] = useState(false);
  
  // API Keys state
  const [apiKeys, setApiKeys] = useState({
    opensky_username: '',
    opensky_password: '',
    adsbx_rapidapi: '',
    flightradar_key: '',
    aisstream_key: '',
    marinetraffic_key: '',
    vesselfinder_key: '',
    copernicus_client_id: '',
    copernicus_client_secret: '',
    nasa_earthdata_token: '',
    planet_api_key: '',
    maxar_api_key: '',
    // OSINT Social Media
    twitter_bearer_token: '',
    twitter_api_key: '',
    twitter_api_secret: '',
    instagram_access_token: '',
    telegram_bot_token: '',
    telegram_api_id: '',
    telegram_api_hash: '',
    tiktok_api_key: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({});
  const [testingApi, setTestingApi] = useState(null);
  const [apiTestResults, setApiTestResults] = useState({});

  // Admin User Management State
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [adminDashboard, setAdminDashboard] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    email: '',
    role: 'analyst',
    full_name: '',
    department: ''
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Provider configurations
  const providers = {
    flight: [
      {
        id: 'opensky',
        name: 'OpenSky Network',
        description: 'Real-time ADS-B aircraft tracking - FREE (4000 calls/day with account)',
        url: 'https://opensky-network.org',
        fields: [
          { key: 'opensky_username', label: 'Username', type: 'text', placeholder: 'your-username' },
          { key: 'opensky_password', label: 'Password', type: 'password', placeholder: 'your-password' }
        ],
        color: 'emerald',
        free: true
      },
      {
        id: 'adsbx',
        name: 'ADS-B Exchange',
        description: 'Unfiltered military aircraft tracking - FREE via RapidAPI',
        url: 'https://rapidapi.com/adsbexchange/api/adsbexchange-com1',
        fields: [
          { key: 'adsbx_rapidapi', label: 'RapidAPI Key', type: 'password', placeholder: 'your-rapidapi-key' }
        ],
        color: 'orange',
        free: true
      },
      {
        id: 'flightradar',
        name: 'FlightRadar24',
        description: 'Premium flight tracking with historical data',
        url: 'https://www.flightradar24.com/premium/api',
        fields: [
          { key: 'flightradar_key', label: 'API Key', type: 'password', placeholder: 'your-api-key' }
        ],
        color: 'yellow',
        free: false
      }
    ],
    vessel: [
      {
        id: 'aisstream',
        name: 'AISStream.io',
        description: 'Real-time AIS vessel tracking via WebSocket - FREE with account',
        url: 'https://aisstream.io',
        fields: [
          { key: 'aisstream_key', label: 'API Key', type: 'password', placeholder: 'your-api-key' }
        ],
        color: 'cyan',
        free: true
      },
      {
        id: 'marinetraffic',
        name: 'MarineTraffic',
        description: 'Global ship tracking and maritime intelligence',
        url: 'https://www.marinetraffic.com/en/ais-api-services',
        fields: [
          { key: 'marinetraffic_key', label: 'API Key', type: 'password', placeholder: 'your-api-key' }
        ],
        color: 'blue',
        free: false
      },
      {
        id: 'vesselfinder',
        name: 'VesselFinder',
        description: 'AIS vessel tracking and maritime data',
        url: 'https://www.vesselfinder.com/api',
        fields: [
          { key: 'vesselfinder_key', label: 'API Key', type: 'password', placeholder: 'your-api-key' }
        ],
        color: 'indigo',
        free: false
      }
    ],
    satellite: [
      {
        id: 'copernicus',
        name: 'Copernicus Data Space',
        description: 'Sentinel-1, Sentinel-2 imagery - FREE with account',
        url: 'https://dataspace.copernicus.eu',
        fields: [
          { key: 'copernicus_client_id', label: 'Client ID', type: 'text', placeholder: 'your-client-id' },
          { key: 'copernicus_client_secret', label: 'Client Secret', type: 'password', placeholder: 'your-client-secret' }
        ],
        color: 'emerald',
        free: true
      },
      {
        id: 'nasa_earthdata',
        name: 'NASA Earthdata',
        description: 'Landsat, MODIS, VIIRS imagery - FREE with account',
        url: 'https://urs.earthdata.nasa.gov',
        fields: [
          { key: 'nasa_earthdata_token', label: 'Bearer Token', type: 'password', placeholder: 'your-bearer-token' }
        ],
        color: 'blue',
        free: true
      },
      {
        id: 'planet',
        name: 'Planet Labs',
        description: 'High-resolution daily imagery (3m)',
        url: 'https://www.planet.com/explorer',
        fields: [
          { key: 'planet_api_key', label: 'API Key', type: 'password', placeholder: 'your-api-key' }
        ],
        color: 'purple',
        free: false
      },
      {
        id: 'maxar',
        name: 'Maxar Technologies',
        description: 'Very high-resolution imagery (30cm)',
        url: 'https://www.maxar.com/products/imagery',
        fields: [
          { key: 'maxar_api_key', label: 'API Key', type: 'password', placeholder: 'your-api-key' }
        ],
        color: 'red',
        free: false
      }
    ],
    osint: [
      {
        id: 'twitter',
        name: 'Twitter/X API',
        description: 'Real-time social media monitoring and sentiment analysis',
        url: 'https://developer.twitter.com/en/portal/dashboard',
        fields: [
          { key: 'twitter_bearer_token', label: 'Bearer Token', type: 'password', placeholder: 'your-bearer-token' },
          { key: 'twitter_api_key', label: 'API Key', type: 'password', placeholder: 'your-api-key' },
          { key: 'twitter_api_secret', label: 'API Secret', type: 'password', placeholder: 'your-api-secret' }
        ],
        color: 'cyan',
        free: false
      },
      {
        id: 'instagram',
        name: 'Instagram Graph API',
        description: 'Instagram content analysis via Meta Business API',
        url: 'https://developers.facebook.com/docs/instagram-api',
        fields: [
          { key: 'instagram_access_token', label: 'Access Token', type: 'password', placeholder: 'your-access-token' }
        ],
        color: 'pink',
        free: false
      },
      {
        id: 'telegram',
        name: 'Telegram API',
        description: 'Monitor public channels and groups for intelligence',
        url: 'https://core.telegram.org/api',
        fields: [
          { key: 'telegram_bot_token', label: 'Bot Token', type: 'password', placeholder: 'your-bot-token' },
          { key: 'telegram_api_id', label: 'API ID', type: 'text', placeholder: 'your-api-id' },
          { key: 'telegram_api_hash', label: 'API Hash', type: 'password', placeholder: 'your-api-hash' }
        ],
        color: 'blue',
        free: true
      },
      {
        id: 'tiktok',
        name: 'TikTok Research API',
        description: 'Access public TikTok content for research purposes',
        url: 'https://developers.tiktok.com',
        fields: [
          { key: 'tiktok_api_key', label: 'API Key', type: 'password', placeholder: 'your-api-key' }
        ],
        color: 'purple',
        free: false
      }
    ]
  };

  // Default news sources (built-in)
  const defaultNewsSources = useMemo(() => [
    { name: 'Defense News', url: 'https://www.defensenews.com/arc/outboundfeeds/rss/', category: 'Defense', enabled: true },
    { name: 'Military Times', url: 'https://www.militarytimes.com/arc/outboundfeeds/rss/', category: 'Military', enabled: true },
    { name: 'BBC World', url: 'https://feeds.bbci.co.uk/news/world/rss.xml', category: 'World News', enabled: true },
    { name: 'Al Jazeera', url: 'https://www.aljazeera.com/xml/rss/all.xml', category: 'Middle East', enabled: true },
    { name: 'Reuters', url: 'https://www.reutersagency.com/feed/', category: 'World News', enabled: true },
    { name: 'Breaking Defense', url: 'https://breakingdefense.com/feed/', category: 'Defense', enabled: true },
    { name: 'War on the Rocks', url: 'https://warontherocks.com/feed/', category: 'Analysis', enabled: true },
    { name: 'Jerusalem Post', url: 'https://www.jpost.com/rss/rssfeedsfrontpage.aspx', category: 'Middle East', enabled: true },
    { name: 'South China Morning Post', url: 'https://www.scmp.com/rss/91/feed', category: 'Asia Pacific', enabled: true },
    { name: 'Kyiv Independent', url: 'https://kyivindependent.com/feed/', category: 'Europe', enabled: true },
    { name: 'CSIS Analysis', url: 'https://www.csis.org/analysis/feed', category: 'Think Tank', enabled: true },
    { name: 'Atlantic Council', url: 'https://www.atlanticcouncil.org/feed/', category: 'Think Tank', enabled: true },
  ], []);

  const loadHealthData = useCallback(async () => {
    try {
      const [healthResponse, alertsResponse] = await Promise.all([
        axios.get(`${API}/health/providers`),
        axios.get(`${API}/health/alerts?limit=20`)
      ]);
      setHealthData(healthResponse.data);
      setHealthAlerts(alertsResponse.data.alerts || []);
    } catch (error) {
      console.error('Error loading health data:', error);
    }
  }, []);

  // Admin User Management Functions
  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadAdminDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`);
      setAdminDashboard(response.data);
    } catch (error) {
      console.error('Error loading admin dashboard:', error);
    }
  }, []);

  const loadActivityLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/activity?limit=50`);
      setActivityLogs(response.data.activities || []);
    } catch (error) {
      console.error('Error loading activity logs:', error);
    }
  }, []);

  const createUser = async () => {
    if (!newUser.username || !newUser.password || !newUser.email) {
      alert('Please fill in all required fields');
      return;
    }
    
    setCreatingUser(true);
    try {
      const response = await axios.post(`${API}/admin/users/create`, newUser);
      if (response.data.success) {
        setUsers(prev => [...prev, response.data.user]);
        setShowCreateUser(false);
        setNewUser({ username: '', password: '', email: '', role: 'analyst', full_name: '', department: '' });
        loadAdminDashboard();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert(error.response?.data?.detail || 'Error creating user');
    } finally {
      setCreatingUser(false);
    }
  };

  const deleteUser = async (userId, username) => {
    if (!confirm(`Are you sure you want to delete user "${username}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      loadAdminDashboard();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Error deleting user');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { is_active: !currentStatus });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
      loadAdminDashboard();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  useEffect(() => {
    loadSettings();
    loadDataSourcesStatus();
    loadHealthData();
    loadUsers();
    loadAdminDashboard();
    loadActivityLogs();
    
    // Refresh health data every 60 seconds
    const interval = setInterval(loadHealthData, 60000);
    return () => clearInterval(interval);
  }, [loadHealthData, loadUsers, loadAdminDashboard, loadActivityLogs]);

  const loadDataSourcesStatus = async () => {
    try {
      const response = await axios.get(`${API}/data-sources/status`);
      setDataSources(response.data);
    } catch (error) {
      console.error('Error loading data sources status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setOfflineMode(response.data.offline_mode || false);
      setLanguage(response.data.language || 'en');
      
      if (response.data.api_keys) {
        setApiKeys(prev => ({ ...prev, ...response.data.api_keys }));
      }
      
      if (response.data.custom_sources) {
        setCustomNewsSources(response.data.custom_sources);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings`, {
        offline_mode: offlineMode,
        language: language,
        api_keys: apiKeys,
        custom_sources: customNewsSources
      });
      
      localStorage.setItem('defensewatch_language', language);
      if (offlineMode) {
        localStorage.setItem('defensewatch_offline', 'true');
      } else {
        localStorage.removeItem('defensewatch_offline');
      }
      
      alert('Settings saved successfully!');
      loadDataSourcesStatus();
      loadHealthData();
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Add custom news source
  const addCustomSource = async () => {
    if (!newSourceName.trim() || !newSourceUrl.trim()) {
      alert('Please enter both name and RSS URL');
      return;
    }
    
    // Basic URL validation
    if (!newSourceUrl.startsWith('http://') && !newSourceUrl.startsWith('https://')) {
      alert('Please enter a valid URL starting with http:// or https://');
      return;
    }
    
    setAddingSource(true);
    try {
      // Test the RSS feed first
      const response = await axios.post(`${API}/news/test-source`, {
        name: newSourceName,
        url: newSourceUrl
      });
      
      if (response.data.valid) {
        const newSource = {
          name: newSourceName,
          url: newSourceUrl,
          category: 'Custom',
          enabled: true,
          articles_found: response.data.articles_count || 0
        };
        setCustomNewsSources(prev => [...prev, newSource]);
        setNewSourceName('');
        setNewSourceUrl('');
        
        // Auto-save
        await axios.post(`${API}/settings`, {
          offline_mode: offlineMode,
          language: language,
          api_keys: apiKeys,
          custom_sources: [...customNewsSources, newSource]
        });
      } else {
        alert(response.data.error || 'Invalid RSS feed. Please check the URL.');
      }
    } catch (error) {
      console.error('Error adding source:', error);
      alert('Failed to validate RSS feed. Please check the URL.');
    } finally {
      setAddingSource(false);
    }
  };

  // Remove custom news source
  const removeCustomSource = async (index) => {
    const updated = customNewsSources.filter((_, i) => i !== index);
    setCustomNewsSources(updated);
    
    // Auto-save
    try {
      await axios.post(`${API}/settings`, {
        offline_mode: offlineMode,
        language: language,
        api_keys: apiKeys,
        custom_sources: updated
      });
    } catch (error) {
      console.error('Error removing source:', error);
    }
  };

  // Toggle news source enabled/disabled
  const toggleSourceEnabled = async (index, isCustom = false) => {
    if (isCustom) {
      const updated = [...customNewsSources];
      updated[index].enabled = !updated[index].enabled;
      setCustomNewsSources(updated);
      
      // Auto-save
      try {
        await axios.post(`${API}/settings`, {
          offline_mode: offlineMode,
          language: language,
          api_keys: apiKeys,
          custom_sources: updated
        });
      } catch (error) {
        console.error('Error toggling source:', error);
      }
    }
  };

  const configureProvider = async (providerId, providerType) => {
    setTestingApi(providerId);
    
    try {
      let endpoint = '';
      let payload = {};
      
      if (providerId === 'aisstream') {
        endpoint = `${API}/config/aisstream`;
        payload = { api_key: apiKeys.aisstream_key };
      } else if (providerId === 'adsbx') {
        endpoint = `${API}/config/adsbx`;
        payload = { api_key: apiKeys.adsbx_rapidapi };
      } else {
        endpoint = `${API}/config/provider`;
        payload = { provider_id: providerId, type: providerType, keys: {} };
        
        const provider = [...providers.flight, ...providers.vessel, ...providers.satellite]
          .find(p => p.id === providerId);
        if (provider) {
          provider.fields.forEach(field => {
            payload.keys[field.key] = apiKeys[field.key];
          });
        }
      }
      
      const response = await axios.post(endpoint, payload);
      
      setApiTestResults(prev => ({
        ...prev,
        [providerId]: { success: true, message: response.data.message || 'Connected successfully!' }
      }));
      
      loadDataSourcesStatus();
      loadHealthData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail;
      let message = 'Connection failed';
      if (typeof errorMsg === 'string') {
        message = errorMsg;
      } else if (Array.isArray(errorMsg)) {
        message = errorMsg.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (typeof errorMsg === 'object') {
        message = JSON.stringify(errorMsg);
      }
      
      setApiTestResults(prev => ({
        ...prev,
        [providerId]: { success: false, message }
      }));
    } finally {
      setTestingApi(null);
    }
  };

  const checkProviderHealth = async (providerId) => {
    setCheckingHealth(providerId);
    try {
      const response = await axios.post(`${API}/health/check/${providerId}`);
      loadHealthData();
      
      setApiTestResults(prev => ({
        ...prev,
        [providerId]: { 
          success: response.data.status === 'healthy',
          message: response.data.status === 'healthy' 
            ? `Healthy (${response.data.response_time_ms}ms)` 
            : response.data.error_message || 'Check failed'
        }
      }));
    } catch (error) {
      console.error('Health check error:', error);
    } finally {
      setCheckingHealth(null);
    }
  };

  const removeProvider = async (providerId) => {
    try {
      await axios.delete(`${API}/config/provider/${providerId}`);
      
      const provider = [...providers.flight, ...providers.vessel, ...providers.satellite]
        .find(p => p.id === providerId);
      if (provider) {
        const clearedKeys = {};
        provider.fields.forEach(field => {
          clearedKeys[field.key] = '';
        });
        setApiKeys(prev => ({ ...prev, ...clearedKeys }));
      }
      
      setApiTestResults(prev => {
        const updated = { ...prev };
        delete updated[providerId];
        return updated;
      });
      
      loadDataSourcesStatus();
      loadHealthData();
    } catch (error) {
      console.error('Error removing provider:', error);
    }
  };

  const dismissAllAlerts = async () => {
    try {
      await axios.post(`${API}/health/alerts/dismiss`, { dismiss_all: true });
      setHealthAlerts([]);
    } catch (error) {
      console.error('Error dismissing alerts:', error);
    }
  };

  const togglePasswordVisibility = (key) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getProviderHealth = (providerId) => {
    if (!healthData?.providers) return null;
    return healthData.providers.find(p => p.provider_id === providerId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-emerald-400';
      case 'degraded': return 'text-yellow-400';
      case 'failed': case 'rate_limited': return 'text-red-400';
      default: return 'text-zinc-400';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'healthy': return 'bg-emerald-500/10 border-emerald-500/30';
      case 'degraded': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'failed': case 'rate_limited': return 'bg-red-500/10 border-red-500/30';
      default: return 'bg-zinc-500/10 border-zinc-500/30';
    }
  };

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'border-emerald-500/30 bg-emerald-500/5',
      orange: 'border-orange-500/30 bg-orange-500/5',
      yellow: 'border-yellow-500/30 bg-yellow-500/5',
      cyan: 'border-cyan-500/30 bg-cyan-500/5',
      blue: 'border-blue-500/30 bg-blue-500/5',
      indigo: 'border-indigo-500/30 bg-indigo-500/5',
      purple: 'border-purple-500/30 bg-purple-500/5',
      red: 'border-red-500/30 bg-red-500/5',
      pink: 'border-pink-500/30 bg-pink-500/5'
    };
    return colors[color] || 'border-white/10 bg-white/5';
  };

  const getTextColor = (color) => {
    const colors = {
      emerald: 'text-emerald-400',
      orange: 'text-orange-400',
      yellow: 'text-yellow-400',
      cyan: 'text-cyan-400',
      blue: 'text-blue-400',
      indigo: 'text-indigo-400',
      purple: 'text-purple-400',
      red: 'text-red-400',
      pink: 'text-pink-400'
    };
    return colors[color] || 'text-white';
  };

  const getBtnClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50 hover:bg-emerald-500/30',
      orange: 'bg-orange-500/20 text-orange-400 border-orange-500/50 hover:bg-orange-500/30',
      yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50 hover:bg-yellow-500/30',
      cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50 hover:bg-cyan-500/30',
      blue: 'bg-blue-500/20 text-blue-400 border-blue-500/50 hover:bg-blue-500/30',
      indigo: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/50 hover:bg-indigo-500/30',
      purple: 'bg-purple-500/20 text-purple-400 border-purple-500/50 hover:bg-purple-500/30',
      red: 'bg-red-500/20 text-red-400 border-red-500/50 hover:bg-red-500/30',
      pink: 'bg-pink-500/20 text-pink-400 border-pink-500/50 hover:bg-pink-500/30'
    };
    return colors[color] || 'bg-white/10 text-white border-white/30 hover:bg-white/20';
  };

  const isProviderConfigured = (providerId) => {
    if (providerId === 'opensky') return dataSources?.aircraft?.status === 'active';
    if (providerId === 'adsbx') return dataSources?.military?.api_configured;
    if (providerId === 'aisstream') return dataSources?.vessels?.api_configured;
    return false;
  };

  // Mini sparkline for health history
  const HealthSparkline = ({ history }) => {
    if (!history || history.length === 0) return null;
    const maxTime = Math.max(...history.map(h => h.response_time_ms || 0), 1);
    
    return (
      <div className="flex items-end gap-0.5 h-4">
        {history.slice(-10).map((h, i) => (
          <div
            key={i}
            className={`w-1 ${h.status === 'healthy' ? 'bg-emerald-500' : h.status === 'degraded' ? 'bg-yellow-500' : 'bg-red-500'}`}
            style={{ height: `${Math.max(20, (h.response_time_ms / maxTime) * 100)}%` }}
            title={`${h.response_time_ms}ms - ${h.status}`}
          />
        ))}
      </div>
    );
  };

  const renderProviderCard = (provider, type) => {
    const configured = isProviderConfigured(provider.id);
    const testResult = apiTestResults[provider.id];
    const isTesting = testingApi === provider.id;
    const isCheckingHealth = checkingHealth === provider.id;
    const health = getProviderHealth(provider.id);
    
    // Get appropriate icon
    const getIcon = () => {
      if (type === 'flight') return <Plane className="w-5 h-5" />;
      if (type === 'vessel') return <Ship className="w-5 h-5" />;
      if (type === 'satellite') return <Satellite className="w-5 h-5" />;
      if (type === 'osint') {
        if (provider.id === 'twitter') return <Twitter className="w-5 h-5" />;
        if (provider.id === 'instagram') return <InstagramIcon className="w-5 h-5" />;
        if (provider.id === 'telegram') return <Send className="w-5 h-5" />;
        if (provider.id === 'tiktok') return <MessageCircle className="w-5 h-5" />;
        return <Search className="w-5 h-5" />;
      }
      return <Globe className="w-5 h-5" />;
    };
    
    return (
      <div 
        key={provider.id}
        className={`p-4 border transition-all ${getColorClasses(provider.color)}`}
        data-testid={`provider-${provider.id}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className={`text-lg ${getTextColor(provider.color)}`}>
              {getIcon()}
            </span>
            <div>
              <h3 className="text-sm font-bold flex items-center gap-2">
                {provider.name}
                {provider.free && (
                  <span className="px-2 py-0.5 text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                    FREE
                  </span>
                )}
              </h3>
              <p className="text-xs text-zinc-400 mt-0.5">{provider.description}</p>
            </div>
          </div>
          
          {/* Status indicator with health */}
          <div className="flex flex-col items-end gap-1">
            {configured ? (
              <span className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                <CheckCircle className="w-3 h-3" />
                CONNECTED
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono bg-zinc-500/10 text-zinc-400 border border-zinc-500/30">
                <WifiOff className="w-3 h-3" />
                NOT CONFIGURED
              </span>
            )}
            
            {/* Health status */}
            {health && (
              <span className={`flex items-center gap-1 px-2 py-1 text-[10px] font-mono border ${getStatusBgColor(health.status)}`}>
                <Heart className={`w-3 h-3 ${getStatusColor(health.status)}`} />
                <span className={getStatusColor(health.status)}>{health.status?.toUpperCase()}</span>
              </span>
            )}
          </div>
        </div>

        {/* Health metrics row */}
        {health && configured && (
          <div className="mb-3 p-2 bg-black/20 border border-white/5 grid grid-cols-4 gap-2 text-[10px]">
            <div>
              <span className="text-zinc-500 block">Uptime</span>
              <span className={health.uptime_percent >= 99 ? 'text-emerald-400' : health.uptime_percent >= 95 ? 'text-yellow-400' : 'text-red-400'}>
                {health.uptime_percent}%
              </span>
            </div>
            <div>
              <span className="text-zinc-500 block">Avg Response</span>
              <span className="text-white">{health.avg_response_time_ms}ms</span>
            </div>
            <div>
              <span className="text-zinc-500 block">Last Check</span>
              <span className="text-white">
                {health.last_check ? new Date(health.last_check).toLocaleTimeString() : 'Never'}
              </span>
            </div>
            <div>
              <span className="text-zinc-500 block">History</span>
              <HealthSparkline history={health.history} />
            </div>
          </div>
        )}

        {/* API Key Fields */}
        <div className="space-y-3 mb-4">
          {provider.fields.map(field => (
            <div key={field.key}>
              <label className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono mb-1 block">
                {field.label}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type={showPasswords[field.key] ? 'text' : field.type}
                    value={apiKeys[field.key] || ''}
                    onChange={(e) => setApiKeys(prev => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={field.placeholder}
                    className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 text-xs font-mono pr-10"
                    data-testid={`input-${field.key}`}
                  />
                  {field.type === 'password' && (
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility(field.key)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    >
                      {showPasswords[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`mb-3 p-2 text-xs ${testResult.success ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
            {testResult.success ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <XCircle className="w-3 h-3 inline mr-1" />}
            {testResult.message}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <a
            href={provider.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-zinc-400 hover:text-white flex items-center gap-1 font-mono"
          >
            Get API Key <ExternalLink className="w-3 h-3" />
          </a>
          
          <div className="flex gap-2">
            {configured && (
              <>
                <button
                  onClick={() => checkProviderHealth(provider.id)}
                  disabled={isCheckingHealth}
                  className="px-3 py-1.5 text-xs bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/20 transition-all"
                  title="Check Health"
                >
                  {isCheckingHealth ? <Loader className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                </button>
                <button
                  onClick={() => removeProvider(provider.id)}
                  className="px-3 py-1.5 text-xs bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            )}
            <button
              onClick={() => configureProvider(provider.id, type)}
              disabled={isTesting || !provider.fields.some(f => apiKeys[f.key])}
              className={`px-4 py-1.5 text-xs font-bold border transition-all disabled:opacity-50 ${getBtnClasses(provider.color)}`}
              data-testid={`connect-${provider.id}`}
            >
              {isTesting ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : configured ? (
                'UPDATE'
              ) : (
                'CONNECT'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader className="w-8 h-8 text-[#00FF9D] animate-spin" />
      </div>
    );
  }

  const unreadAlerts = healthAlerts.filter(a => !a.read);

  // Tab configuration
  const tabs = [
    { id: 'tracking', label: 'TRACKING', icon: <Globe className="w-4 h-4" />, description: 'Flight, Vessel & Satellite' },
    { id: 'osint', label: 'OSINT', icon: <Search className="w-4 h-4" />, description: 'Social Media Intelligence' },
    { id: 'news', label: 'NEWS SOURCES', icon: <Rss className="w-4 h-4" />, description: 'RSS Feeds & Custom Sources' },
    { id: 'admin', label: 'USER MANAGEMENT', icon: <Users className="w-4 h-4" />, description: 'Manage Users & Activity' },
    { id: 'general', label: 'GENERAL', icon: <Shield className="w-4 h-4" />, description: 'System Settings' }
  ];

  return (
    <div className="h-full overflow-y-auto" data-testid="settings-page">
      {/* Header */}
      <div className="glass-panel border-b border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <SettingsIcon className="w-8 h-8 text-[#00FF9D]" />
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>DATA SOURCES & INTEGRATIONS</h1>
              <p className="text-xs text-zinc-400 font-mono mt-1">Configure all intelligence data providers and sources</p>
            </div>
          </div>
          
          {/* Alerts Button */}
          <button
            onClick={() => setShowAlerts(!showAlerts)}
            className={`relative flex items-center gap-2 px-4 py-2 border transition-all ${
              unreadAlerts.length > 0 
                ? 'bg-red-500/10 text-red-400 border-red-500/30 hover:bg-red-500/20' 
                : 'bg-white/5 text-zinc-400 border-white/10 hover:bg-white/10'
            }`}
          >
            <Bell className="w-4 h-4" />
            <span className="text-xs font-bold">ALERTS</span>
            {unreadAlerts.length > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-[10px] font-bold bg-red-500 text-white rounded-full">
                {unreadAlerts.length}
              </span>
            )}
          </button>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2 text-xs font-bold border transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-[#00FF9D]/10 text-[#00FF9D] border-[#00FF9D]/50'
                  : 'bg-white/5 text-zinc-400 border-white/10 hover:bg-white/10 hover:text-white'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Alerts Dropdown */}
      {showAlerts && (
        <div className="mx-6 mt-4 p-4 bg-black/50 border border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              Health Alerts
            </h3>
            {healthAlerts.length > 0 && (
              <button
                onClick={dismissAllAlerts}
                className="text-xs text-zinc-400 hover:text-white"
              >
                Dismiss All
              </button>
            )}
          </div>
          {healthAlerts.length === 0 ? (
            <div className="text-xs text-zinc-500 text-center py-4">
              No alerts - all systems operational
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {healthAlerts.map((alert, idx) => (
                <div
                  key={idx}
                  className={`p-2 text-xs border ${
                    alert.severity === 'critical' 
                      ? 'bg-red-500/10 border-red-500/30 text-red-400'
                      : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-bold uppercase">{alert.provider}</span>
                    <span className="text-[10px] text-zinc-500">
                      {new Date(alert.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-1 text-zinc-300">{alert.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Health Summary - Show on Tracking tab */}
        {activeTab === 'tracking' && healthData && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-black/30 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-[#00FF9D]" />
                <span className="text-sm font-bold">System Health</span>
              </div>
              <div className="text-2xl font-bold text-[#00FF9D]">
                {healthData.summary?.healthy || 0}/{healthData.summary?.total || 0}
              </div>
              <div className="text-xs text-zinc-400">Providers Healthy</div>
            </div>
            
            <div className="p-4 bg-black/30 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <span className="text-sm font-bold">Healthy</span>
              </div>
              <div className="text-2xl font-bold text-emerald-400">
                {healthData.summary?.healthy || 0}
              </div>
              <div className="text-xs text-zinc-400">Operating normally</div>
            </div>
            
            <div className="p-4 bg-black/30 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <span className="text-sm font-bold">Degraded</span>
              </div>
              <div className="text-2xl font-bold text-yellow-400">
                {healthData.summary?.degraded || 0}
              </div>
              <div className="text-xs text-zinc-400">Performance issues</div>
            </div>
            
            <div className="p-4 bg-black/30 border border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-sm font-bold">Failed</span>
              </div>
              <div className="text-2xl font-bold text-red-400">
                {healthData.summary?.failed || 0}
              </div>
              <div className="text-xs text-zinc-400">Require attention</div>
            </div>
          </div>
        )}

        {/* TRACKING TAB */}
        {activeTab === 'tracking' && (
          <>
            {/* Current Status Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-black/30 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Plane className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm font-bold">Flight Tracking</span>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Primary:</span>
                    <span className={dataSources?.aircraft?.status === 'active' ? 'text-emerald-400' : 'text-yellow-400'}>
                      {dataSources?.aircraft?.source || 'Not Configured'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Military:</span>
                    <span className={dataSources?.military?.api_configured ? 'text-emerald-400' : 'text-zinc-500'}>
                      {dataSources?.military?.api_configured ? 'ADS-B Exchange' : 'Not Configured'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-black/30 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Ship className="w-5 h-5 text-cyan-400" />
                  <span className="text-sm font-bold">Vessel Tracking</span>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Source:</span>
                    <span className={dataSources?.vessels?.api_configured ? 'text-emerald-400' : 'text-yellow-400'}>
                      {dataSources?.vessels?.api_configured ? 'AISStream.io' : 'Simulation'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Status:</span>
                    <span className={dataSources?.vessels?.status === 'active' ? 'text-emerald-400' : 'text-yellow-400'}>
                      {dataSources?.vessels?.status?.toUpperCase() || 'OFFLINE'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-black/30 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Satellite className="w-5 h-5 text-purple-400" />
                  <span className="text-sm font-bold">Satellite Imagery</span>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Source:</span>
                    <span className="text-yellow-400">ESRI World Imagery</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Note:</span>
                    <span className="text-zinc-500">Configure providers for more data</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Flight Tracking Providers */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Plane className="w-6 h-6 text-emerald-400" />
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>FLIGHT TRACKING PROVIDERS</h2>
              </div>
              <p className="text-xs text-zinc-400 mb-4">
                Connect to aircraft tracking APIs for real-time ADS-B data including civilian and military flights
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {providers.flight.map(provider => renderProviderCard(provider, 'flight'))}
              </div>
            </div>

            {/* Vessel Tracking Providers */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Ship className="w-6 h-6 text-cyan-400" />
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>VESSEL TRACKING PROVIDERS</h2>
              </div>
              <p className="text-xs text-zinc-400 mb-4">
                Connect to AIS data providers for real-time global vessel tracking and maritime intelligence
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {providers.vessel.map(provider => renderProviderCard(provider, 'vessel'))}
              </div>
            </div>

            {/* Satellite Imagery Providers */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Satellite className="w-6 h-6 text-purple-400" />
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>SATELLITE IMAGERY PROVIDERS</h2>
              </div>
              <p className="text-xs text-zinc-400 mb-4">
                Connect to satellite imagery APIs for real Earth observation data from multiple providers
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {providers.satellite.map(provider => renderProviderCard(provider, 'satellite'))}
              </div>
            </div>
          </>
        )}

        {/* OSINT TAB */}
        {activeTab === 'osint' && (
          <>
            {/* OSINT Status Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-black/30 border border-cyan-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Twitter className="w-5 h-5 text-cyan-400" />
                  <span className="text-sm font-bold">Twitter/X</span>
                </div>
                <div className={`text-lg font-bold ${apiKeys.twitter_bearer_token ? 'text-emerald-400' : 'text-zinc-500'}`}>
                  {apiKeys.twitter_bearer_token ? 'CONFIGURED' : 'NOT SET'}
                </div>
                <div className="text-xs text-zinc-400">Social monitoring</div>
              </div>
              
              <div className="p-4 bg-black/30 border border-pink-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <InstagramIcon className="w-5 h-5 text-pink-400" />
                  <span className="text-sm font-bold">Instagram</span>
                </div>
                <div className={`text-lg font-bold ${apiKeys.instagram_access_token ? 'text-emerald-400' : 'text-zinc-500'}`}>
                  {apiKeys.instagram_access_token ? 'CONFIGURED' : 'NOT SET'}
                </div>
                <div className="text-xs text-zinc-400">Visual intelligence</div>
              </div>
              
              <div className="p-4 bg-black/30 border border-blue-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <Send className="w-5 h-5 text-blue-400" />
                  <span className="text-sm font-bold">Telegram</span>
                </div>
                <div className={`text-lg font-bold ${apiKeys.telegram_bot_token ? 'text-emerald-400' : 'text-zinc-500'}`}>
                  {apiKeys.telegram_bot_token ? 'CONFIGURED' : 'NOT SET'}
                </div>
                <div className="text-xs text-zinc-400">Channel monitoring</div>
              </div>
              
              <div className="p-4 bg-black/30 border border-purple-500/20">
                <div className="flex items-center gap-2 mb-2">
                  <MessageCircle className="w-5 h-5 text-purple-400" />
                  <span className="text-sm font-bold">TikTok</span>
                </div>
                <div className={`text-lg font-bold ${apiKeys.tiktok_api_key ? 'text-emerald-400' : 'text-zinc-500'}`}>
                  {apiKeys.tiktok_api_key ? 'CONFIGURED' : 'NOT SET'}
                </div>
                <div className="text-xs text-zinc-400">Research API</div>
              </div>
            </div>

            {/* OSINT Social Media Providers */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Search className="w-6 h-6 text-[#00FF9D]" />
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>SOCIAL MEDIA INTELLIGENCE</h2>
              </div>
              <p className="text-xs text-zinc-400 mb-4">
                Connect to social media platforms for real-time OSINT data collection and analysis
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {providers.osint.map(provider => renderProviderCard(provider, 'osint'))}
              </div>
            </div>
          </>
        )}

        {/* NEWS SOURCES TAB */}
        {activeTab === 'news' && (
          <>
            {/* News Status */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-black/30 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Rss className="w-5 h-5 text-orange-400" />
                  <span className="text-sm font-bold">Built-in Sources</span>
                </div>
                <div className="text-2xl font-bold text-orange-400">{defaultNewsSources.length}</div>
                <div className="text-xs text-zinc-400">Active RSS feeds</div>
              </div>
              
              <div className="p-4 bg-black/30 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Plus className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm font-bold">Custom Sources</span>
                </div>
                <div className="text-2xl font-bold text-emerald-400">{customNewsSources.length}</div>
                <div className="text-xs text-zinc-400">User-added feeds</div>
              </div>
              
              <div className="p-4 bg-black/30 border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="w-5 h-5 text-[#00FF9D]" />
                  <span className="text-sm font-bold">Total Active</span>
                </div>
                <div className="text-2xl font-bold text-[#00FF9D]">
                  {defaultNewsSources.length + customNewsSources.filter(s => s.enabled).length}
                </div>
                <div className="text-xs text-zinc-400">Combined sources</div>
              </div>
            </div>

            {/* Add Custom Source */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Plus className="w-6 h-6 text-emerald-400" />
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>ADD CUSTOM NEWS SOURCE</h2>
              </div>
              <p className="text-xs text-zinc-400 mb-4">
                Add your own RSS feed URLs to include in the news aggregation
              </p>
              
              <div className="flex gap-3 mb-4">
                <input
                  type="text"
                  value={newSourceName}
                  onChange={(e) => setNewSourceName(e.target.value)}
                  placeholder="Source Name (e.g., My News Source)"
                  className="flex-1 bg-black/50 border border-white/10 text-white px-4 py-3 text-sm font-mono"
                  data-testid="new-source-name"
                />
                <input
                  type="url"
                  value={newSourceUrl}
                  onChange={(e) => setNewSourceUrl(e.target.value)}
                  placeholder="RSS Feed URL (e.g., https://example.com/rss.xml)"
                  className="flex-[2] bg-black/50 border border-white/10 text-white px-4 py-3 text-sm font-mono"
                  data-testid="new-source-url"
                />
                <button
                  onClick={addCustomSource}
                  disabled={addingSource || !newSourceName.trim() || !newSourceUrl.trim()}
                  className="flex items-center gap-2 px-6 py-3 bg-emerald-500/20 text-emerald-400 border border-emerald-500/50 hover:bg-emerald-500/30 transition-all disabled:opacity-50"
                  data-testid="add-source-btn"
                >
                  {addingSource ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  <span className="font-bold text-sm">ADD SOURCE</span>
                </button>
              </div>
            </div>

            {/* Built-in News Sources */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Rss className="w-6 h-6 text-orange-400" />
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>BUILT-IN NEWS SOURCES</h2>
                <span className="px-2 py-1 text-[10px] font-mono bg-orange-500/10 text-orange-400 border border-orange-500/30">
                  {defaultNewsSources.length} SOURCES
                </span>
              </div>
              <p className="text-xs text-zinc-400 mb-4">
                Pre-configured trusted news sources for defense, military, and geopolitical intelligence
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto">
                {defaultNewsSources.map((source, idx) => (
                  <div key={idx} className="p-3 bg-black/30 border border-white/10 hover:border-orange-500/30 transition-all">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Rss className="w-4 h-4 text-orange-400 flex-shrink-0" />
                        <span className="text-sm font-bold truncate">{source.name}</span>
                      </div>
                      <span className="px-2 py-0.5 text-[9px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                        ACTIVE
                      </span>
                    </div>
                    <div className="text-[10px] text-zinc-500 mb-1 truncate">{source.url}</div>
                    <div className="text-[10px] text-zinc-400">
                      Category: <span className="text-zinc-300">{source.category}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom News Sources */}
            {customNewsSources.length > 0 && (
              <div className="glass-panel tactical-border p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Plus className="w-6 h-6 text-emerald-400" />
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>CUSTOM NEWS SOURCES</h2>
                  <span className="px-2 py-1 text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
                    {customNewsSources.length} SOURCES
                  </span>
                </div>
                
                <div className="space-y-3">
                  {customNewsSources.map((source, idx) => (
                    <div key={idx} className="p-4 bg-black/30 border border-emerald-500/20 hover:border-emerald-500/40 transition-all">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Rss className={`w-5 h-5 ${source.enabled ? 'text-emerald-400' : 'text-zinc-500'}`} />
                          <div>
                            <div className="font-bold text-sm">{source.name}</div>
                            <div className="text-[10px] text-zinc-500 font-mono">{source.url}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {source.articles_found > 0 && (
                            <span className="text-[10px] text-zinc-400">
                              {source.articles_found} articles found
                            </span>
                          )}
                          <button
                            onClick={() => toggleSourceEnabled(idx, true)}
                            className={`px-3 py-1.5 text-xs border transition-all ${
                              source.enabled 
                                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' 
                                : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30'
                            }`}
                          >
                            {source.enabled ? 'ENABLED' : 'DISABLED'}
                          </button>
                          <button
                            onClick={() => removeCustomSource(idx)}
                            className="p-1.5 text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/30 transition-all"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ADMIN USER MANAGEMENT TAB */}
        {activeTab === 'admin' && (
          <div className="space-y-6">
            {/* Admin Dashboard Stats */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-6 h-6 text-[#00FF9D]" />
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>ADMIN DASHBOARD</h2>
                </div>
                <button
                  onClick={() => { loadUsers(); loadAdminDashboard(); loadActivityLogs(); }}
                  className="px-3 py-1 text-xs bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/30 hover:bg-[#00FF9D]/20"
                >
                  <RefreshCw className="w-3 h-3 inline mr-1" /> Refresh
                </button>
              </div>

              {adminDashboard && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-lg text-center">
                    <Users className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-emerald-400">{adminDashboard.stats?.total_users || 0}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">Total Users</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-cyan-500/10 to-transparent border border-cyan-500/20 rounded-lg text-center">
                    <UserCheck className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-cyan-400">{adminDashboard.stats?.active_users || 0}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">Active Users</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-lg text-center">
                    <Activity className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-purple-400">{adminDashboard.stats?.activity_24h || 0}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">Activity (24h)</div>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg text-center">
                    <Shield className="w-6 h-6 text-orange-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-orange-400">{adminDashboard.stats?.reports_generated || 0}</div>
                    <div className="text-[10px] text-zinc-500 uppercase">Reports Gen.</div>
                  </div>
                </div>
              )}
            </div>

            {/* User Management Section */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-cyan-400" />
                  <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>USER MANAGEMENT</h2>
                  <span className="px-2 py-0.5 text-[10px] bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                    {users.length} USERS
                  </span>
                </div>
                <button
                  onClick={() => setShowCreateUser(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50 hover:bg-[#00FF9D]/20"
                  data-testid="create-user-btn"
                >
                  <UserPlus className="w-4 h-4" />
                  <span className="text-sm font-bold">ADD USER</span>
                </button>
              </div>

              {/* Create User Modal */}
              {showCreateUser && (
                <div className="mb-6 p-4 bg-black/40 border border-[#00FF9D]/30 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-[#00FF9D]">Create New User</h3>
                    <button onClick={() => setShowCreateUser(false)} className="text-zinc-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Username *</label>
                      <input
                        type="text"
                        value={newUser.username}
                        onChange={(e) => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                        className="w-full p-2 bg-black/50 border border-white/10 text-white text-sm"
                        placeholder="Enter username"
                        data-testid="new-user-username"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Password *</label>
                      <input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                        className="w-full p-2 bg-black/50 border border-white/10 text-white text-sm"
                        placeholder="Enter password"
                        data-testid="new-user-password"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Email *</label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full p-2 bg-black/50 border border-white/10 text-white text-sm"
                        placeholder="user@example.com"
                        data-testid="new-user-email"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Role</label>
                      <select
                        value={newUser.role}
                        onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                        className="w-full p-2 bg-black/50 border border-white/10 text-white text-sm"
                        data-testid="new-user-role"
                      >
                        <option value="admin">Admin</option>
                        <option value="analyst">Analyst</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Full Name</label>
                      <input
                        type="text"
                        value={newUser.full_name}
                        onChange={(e) => setNewUser(prev => ({ ...prev, full_name: e.target.value }))}
                        className="w-full p-2 bg-black/50 border border-white/10 text-white text-sm"
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase mb-1 block">Department</label>
                      <input
                        type="text"
                        value={newUser.department}
                        onChange={(e) => setNewUser(prev => ({ ...prev, department: e.target.value }))}
                        className="w-full p-2 bg-black/50 border border-white/10 text-white text-sm"
                        placeholder="IMINT Division"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      onClick={() => setShowCreateUser(false)}
                      className="px-4 py-2 bg-zinc-700/50 text-zinc-300 border border-zinc-600 text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createUser}
                      disabled={creatingUser}
                      className="px-4 py-2 bg-[#00FF9D]/20 text-[#00FF9D] border border-[#00FF9D]/50 text-sm disabled:opacity-50"
                      data-testid="create-user-submit"
                    >
                      {creatingUser ? <Loader className="w-4 h-4 animate-spin" /> : 'Create User'}
                    </button>
                  </div>
                </div>
              )}

              {/* User List */}
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 text-[#00FF9D] animate-spin" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4 text-zinc-500 uppercase text-[10px]">User</th>
                        <th className="text-left py-3 px-4 text-zinc-500 uppercase text-[10px]">Email</th>
                        <th className="text-center py-3 px-4 text-zinc-500 uppercase text-[10px]">Role</th>
                        <th className="text-center py-3 px-4 text-zinc-500 uppercase text-[10px]">Department</th>
                        <th className="text-center py-3 px-4 text-zinc-500 uppercase text-[10px]">Status</th>
                        <th className="text-center py-3 px-4 text-zinc-500 uppercase text-[10px]">Activity</th>
                        <th className="text-right py-3 px-4 text-zinc-500 uppercase text-[10px]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${user.username}`}>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                user.role === 'admin' ? 'bg-red-500/20 text-red-400' :
                                user.role === 'analyst' ? 'bg-cyan-500/20 text-cyan-400' :
                                'bg-zinc-500/20 text-zinc-400'
                              }`}>
                                {user.username?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="font-bold text-white">{user.full_name || user.username}</div>
                                <div className="text-[10px] text-zinc-500 font-mono">@{user.username}</div>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-zinc-400 font-mono text-xs">{user.email}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-1 text-[10px] rounded ${
                              user.role === 'admin' ? 'bg-red-500/20 text-red-400 border border-red-500/30' :
                              user.role === 'analyst' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' :
                              'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                            }`}>
                              {user.role?.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-zinc-400 text-xs">{user.department || '-'}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-1 text-[10px] rounded ${
                              user.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {user.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-zinc-400 text-xs">{user.activity_count || 0}</td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => toggleUserStatus(user.id, user.is_active)}
                                className={`p-1.5 rounded ${user.is_active ? 'bg-yellow-500/20 text-yellow-400' : 'bg-emerald-500/20 text-emerald-400'}`}
                                title={user.is_active ? 'Deactivate' : 'Activate'}
                              >
                                {user.is_active ? <UserMinus className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                              </button>
                              {user.username !== 'RSU' && (
                                <button
                                  onClick={() => deleteUser(user.id, user.username)}
                                  className="p-1.5 rounded bg-red-500/20 text-red-400"
                                  title="Delete User"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                      {users.length === 0 && (
                        <tr>
                          <td colSpan="7" className="py-8 text-center text-zinc-500">
                            No users found. Click "Add User" to create one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Activity Logs Section */}
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center gap-3 mb-4">
                <Activity className="w-6 h-6 text-purple-400" />
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>ACTIVITY LOGS</h2>
                <span className="px-2 py-0.5 text-[10px] bg-purple-500/20 text-purple-400 border border-purple-500/30">
                  LAST 50 ACTIONS
                </span>
              </div>

              <div className="max-h-[300px] overflow-y-auto">
                {activityLogs.length > 0 ? (
                  <div className="space-y-2">
                    {activityLogs.map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-black/20 border border-white/5 rounded">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            log.action?.includes('CREATE') ? 'bg-emerald-400' :
                            log.action?.includes('DELETE') ? 'bg-red-400' :
                            log.action?.includes('UPDATE') ? 'bg-yellow-400' :
                            log.action?.includes('LOGIN') ? 'bg-cyan-400' :
                            'bg-zinc-400'
                          }`}></div>
                          <div>
                            <span className="text-xs text-white font-mono">{log.action}</span>
                            <p className="text-[10px] text-zinc-500">{log.details}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] text-zinc-500 font-mono">
                            {new Date(log.timestamp).toLocaleString()}
                          </div>
                          <div className="text-[9px] text-zinc-600">{log.user_id}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    No activity logs available
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* GENERAL TAB */}
        {activeTab === 'general' && (
          <div className="glass-panel tactical-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-6 h-6 text-[#00FF9D]" />
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>GENERAL SETTINGS</h2>
            </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-black/20 border border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold uppercase mb-1">Offline Mode</h3>
                  <p className="text-xs text-zinc-400">
                    Cache data locally, disable external API calls
                  </p>
                  {offlineMode && (
                    <div className="mt-2 flex items-center gap-2 text-yellow-400 text-xs">
                      <AlertTriangle className="w-4 h-4" />
                      <span className="font-mono">Live updates disabled</span>
                    </div>
                  )}
                </div>
                <button
                  data-testid="offline-mode-toggle"
                  onClick={() => setOfflineMode(!offlineMode)}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    offlineMode ? 'bg-[#00FF9D]' : 'bg-zinc-700'
                  }`}
                >
                  <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    offlineMode ? 'translate-x-8' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            </div>

            <div className="p-4 bg-black/20 border border-white/10">
              <h3 className="text-sm font-bold uppercase mb-2">Language</h3>
              <select
                data-testid="language-select"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full bg-black/50 border border-white/10 text-white px-4 py-2 text-sm font-mono"
              >
                <option value="en">English</option>
                <option value="ar">العربية (Arabic)</option>
              </select>
            </div>
          </div>
          </div>
        )}

        {/* Save Button */}
        <div className="sticky bottom-0 glass-panel border-t border-white/10 p-6 flex justify-between items-center">
          <button
            onClick={() => { loadDataSourcesStatus(); loadHealthData(); }}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10 transition-all text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            REFRESH STATUS
          </button>
          
          <button
            data-testid="save-settings-button"
            onClick={saveSettings}
            disabled={saving}
            className="flex items-center gap-2 px-8 py-3 btn-primary disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>SAVING...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>SAVE ALL SETTINGS</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
